import { useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  isModuleEnabled as isModuleEnabledFn,
  isAssessmentAccessActive,
  type ModuleKey,
} from "@/lib/modules";
import {
  AuthContext,
  type AppRole,
  type Profile,
  type Organisation,
} from "./authContext";

export { useAuth } from "./authContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [activeRole, setActiveRole] = useState<AppRole | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);

  // Server-side source of truth for "can this user start an assessment?"
  // (admin, active-pilot assessor, or organisation module on and not expired).
  const [serverAccess, setServerAccess] = useState<boolean | null>(null);

  const fetchRoles = async (userId: string) => {
    const [{ data }, { data: access }] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", userId),
      supabase.rpc("has_assessment_access", { _user_id: userId }),
    ]);
    const r = (data || []).map((d) => d.role as AppRole);
    setRoles(r.length > 0 ? r : []);
    setServerAccess(typeof access === "boolean" ? access : null);
  };

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("user_type, sector, tier, full_name, organisation, city, borough, ward, area_locality, organisation_id")
      .eq("id", userId)
      .maybeSingle();
    setProfile(data as Profile | null);

    // Fetch organisation if linked
    if (data?.organisation_id) {
      const { data: orgData } = await supabase
        .from("organisations")
        .select("id, name, subscription_tier, module_config, assessment_access_expires_at, assessment_access_source")
        .eq("id", data.organisation_id)
        .maybeSingle();
      setOrganisation(orgData as Organisation | null);
    } else {
      setOrganisation(null);
    }
  };

  const refreshProfile = async () => {
    if (user) await Promise.all([fetchRoles(user.id), fetchProfile(user.id)]);
  };

  // Pick up access changes made by an admin without needing to sign out:
  // re-check when the tab regains focus and every 60 seconds.
  useEffect(() => {
    if (!user) return;
    const refresh = () => {
      if (document.visibilityState === "visible") {
        Promise.all([fetchRoles(user.id), fetchProfile(user.id)]).catch(() => {});
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const t = window.setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(t);
    };
  }, [user?.id]);

  useEffect(() => {
    let isMounted = true;

    const loadUserData = async (userId: string, retries = 2) => {
      try {
        await Promise.all([fetchRoles(userId), fetchProfile(userId)]);
      } catch (err) {
        if (retries > 0 && err instanceof TypeError && err.message.includes("Failed to fetch")) {
          // Backend may still be waking — wait and retry
          await new Promise((r) => setTimeout(r, 2000));
          return loadUserData(userId, retries - 1);
        }
        // ignore other errors
      }
    };

    // Listener for ONGOING auth changes (does NOT control loading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => loadUserData(session.user.id), 0);
        } else {
          setRoles([]);
          setProfile(null);
          setOrganisation(null);
        }
      }
    );

    // Helper: clear all auth state and local storage tokens
    const clearStaleAuth = async () => {
      console.warn("Clearing stale auth state and tokens");
      setSession(null);
      setUser(null);
      setRoles([]);
      setProfile(null);
      setOrganisation(null);
      try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* ignore */ }
      // Also clear any lingering supabase keys from localStorage
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore in environments where localStorage is restricted */ }
    };

    // Warm-up ping: wake the backend (fire-and-forget, never blocks)
    const warmUpBackend = () => {
      try {
        const url = import.meta.env.VITE_SUPABASE_URL;
        if (!url) return;
        const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        fetch(`${url}/rest/v1/`, {
          method: 'HEAD',
          headers: { apikey: key, Authorization: `Bearer ${key}` },
        }).catch(() => { /* non-critical */ });
      } catch { /* non-critical */ }
    };

    // INITIAL load (controls loading) with timeout to prevent infinite hang
    const initializeAuth = async () => {
      // Fire warm-up ping in background — do NOT await
      warmUpBackend();

      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), 6000)
        );
        const result = await Promise.race([sessionPromise, timeoutPromise]);

        if (!isMounted) return;

        if (result && "data" in result) {
          const sess = result.data.session;
          setSession(sess);
          setUser(sess?.user ?? null);
          if (sess?.user) {
            await loadUserData(sess.user.id);
          }
        } else {
          // Timed out — clear stale session and show login
          await clearStaleAuth();
        }
      } catch (err) {
        console.error("Auth init error:", err);
        if (isMounted) {
          // Network-level failure (e.g. "Failed to fetch") — clear stale tokens
          if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
            await clearStaleAuth();
          } else {
            setSession(null);
            setUser(null);
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // If we get a network error, clear stale tokens and retry once
        if (error.message.includes("Failed to fetch")) {
          console.warn("Sign-in failed with network error, clearing stale tokens and retrying...");
          try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* ignore */ }
          try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const key = localStorage.key(i);
              if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
                localStorage.removeItem(key);
              }
            }
          } catch { /* ignore */ }
          // Retry sign-in after clearing
          const { error: retryError } = await supabase.auth.signInWithPassword({ email, password });
          return { error: retryError as Error | null };
        }
        return { error: error as Error | null };
      }
      return { error: null };
    } catch (err) {
      // Catch raw TypeError from fetch failures
      if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
        console.warn("Sign-in threw network error, clearing stale tokens and retrying...");
        try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* ignore */ }
        try {
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
              localStorage.removeItem(key);
            }
          }
        } catch { /* ignore */ }
        const { error: retryError } = await supabase.auth.signInWithPassword({ email, password });
        return { error: retryError as Error | null };
      }
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: metadata,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  const baseAccess = isAssessmentAccessActive(organisation);
  const assessmentAccess =
    serverAccess === null
      ? baseAccess
      : { ...baseAccess, enabled: serverAccess, expired: serverAccess ? false : baseAccess.expired };

  // Assessment honours both toggle and expiry; everything else uses the basic check.
  const isModuleEnabled = (key: ModuleKey) =>
    key === "assessment"
      ? assessmentAccess.enabled
      : isModuleEnabledFn(organisation?.module_config ?? null, key);

  return (
    <AuthContext.Provider value={{ session, user, role: roles[0] || null, roles, activeRole: activeRole ?? roles[0] ?? null, setActiveRole, profile, organisation, loading, signIn, signUp, signOut, hasRole, isModuleEnabled, assessmentAccess, refreshProfile, isAdmin: roles.includes("admin") }}>
      {children}
    </AuthContext.Provider>
  );
}
